// Base Agent Class - Foundation for all agents
// frontend/src/agents/core/BaseAgent.ts

import { AgentCommunicationHub, Message } from './AgentCommunicationHub';

export abstract class BaseAgent {
    id: string;
    type: string;
    commHub: AgentCommunicationHub;

    constructor(id: string, commHub: AgentCommunicationHub, type?: string) {
        this.id = id;
        this.type = type || this.constructor.name;
        this.commHub = commHub;

        // Register with hub
        commHub.registerAgent(id, this);

        console.log(`[${this.id}] Agent created (type: ${this.type})`);
    }

    /**
     * Handle incoming messages
     * Override in subclass to implement agent-specific logic
     */
    async handleMessage(message: Message): Promise<any> {
        console.log(`[${this.id}] Received message from ${message.from}: ${message.type}`);

        // Default implementation - override in subclass
        return {
            status: 'received',
            message: 'Base agent does not implement message handling'
        };
    }

    /**
     * Send a question to another agent
     */
    async ask(targetAgent: string, payload: any): Promise<any> {
        const message: Message = {
            from: this.id,
            to: targetAgent,
            type: 'question',
            payload,
            timestamp: new Date()
        };

        return await this.commHub.sendMessage(message);
    }

    /**
     * Broadcast an event to all agents
     */
    broadcast(payload: any): void {
        const message: Message = {
            from: this.id,
            to: 'broadcast',
            type: 'event',
            payload,
            timestamp: new Date()
        };

        this.commHub.broadcast(message);
    }

    /**
     * Send a command to another agent
     */
    async command(targetAgent: string, payload: any): Promise<any> {
        const message: Message = {
            from: this.id,
            to: targetAgent,
            type: 'command',
            payload,
            timestamp: new Date()
        };

        return await this.commHub.sendMessage(message);
    }

    /**
     * Reply to a message
     */
    async reply(originalMessage: Message, payload: any): Promise<any> {
        const message: Message = {
            from: this.id,
            to: originalMessage.from,
            type: 'answer',
            payload,
            correlationId: originalMessage.correlationId,
            timestamp: new Date()
        };

        return await this.commHub.sendMessage(message);
    }

    /**
     * Shutdown agent
     */
    shutdown(): void {
        console.log(`[${this.id}] Shutting down`);
        this.commHub.unregisterAgent(this.id);
    }
}
